import { useLazyQuery, useMutation } from "@apollo/client";
import { Button, createStyles, Grid, makeStyles, Typography, useMediaQuery } from "@material-ui/core";
import Dialog from "components/Dialog";
import { DefaultInfo } from "components/NothingRight";
import SearchBox from "components/SearchBox";
import { Slider } from "components/Slider/Slider";
import Table from "components/Table";
import { MenuActions } from "components/Table/MenuActions";
import { ROW_PER_PAGE_OPTIONS } from "config";
import { theme } from "config/theme";
import useText from "constants/text";
import { useAlertDispatch } from "contexts/AlertContext";
import { CompanyUser, Profile_User } from "generated/graphql";
import { useCurrentClient } from "hooks";
import { NewUserIcon } from "icons";
import React from "react";
import FilterGroup from "ui/Filter";
import AddUserDialog from "../components/AddUser";
import { UpdateUser } from "../components/UpdateUser";
import { DeleteCompanyUser } from "../graphql/mutations";
import { AllCompanyUsersByClient } from "../graphql/queries";
import { userStyles } from "../styles";
import { useUserListRows } from "../types/Columns";

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      backgroundColor: "#f5f6f8",
      display: "flex",
      flexDirection: "row",
      height: "100%",
      width: "100%",
      paddingTop: "60px",
    },
    left: {
      flex: "70%",
      margin: theme.spacing(2),
    },
    right: {
      flex: "30%",
      margin: theme.spacing(0.2),
      backgroundColor: "#fff",
      padding: theme.spacing(2),
    },
    table: {
      marginLeft: 10,
    },
  })
);
export function Users() {
  const styles = useStyles();
  const { client, loading } = useCurrentClient();
  const dispatchAlert = useAlertDispatch();
  const [user, setUser] = React.useState<CompanyUser>(null);
  const [openAdd, setOpenAdd] = React.useState(false);
  const [isUpdate, update] = React.useState(false);
  const [isDelete, isToDelete] = React.useState(false);
  const [deleteCompanyUser] = useMutation(DeleteCompanyUser);
  const [userToUpdate] = React.useState<CompanyUser>(null);
  const [queryUsers, { data: usersData, loading: userLoading, refetch }] = useLazyQuery(AllCompanyUsersByClient);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [openDrawer, setOpenDrawer] = React.useState(false);
  const [, setSearch] = React.useState(null);
  const [isCurrentUser, currentUser] = React.useState(false);
  const UserColumns = useUserListRows();
  const { text } = useText();
  const style = userStyles();
  const [displayuser, setDisplayUser] = React.useState(false);
  const [page] = React.useState(1);
  const [totalCount] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const onSearch = (event: any) => {
    event.preventDefault();
    event.persist();
    setSearch(event.target.value);
  };

  React.useEffect(() => {
    if (user?.company_user_profil) currentUser(user?.company_user_profil !== Profile_User.Admin);
  }, [user]);

  const actionData = [
    {
      label: text.all.edit,
      key: "1",
    },
    {
      label: text.all.delete,
      key: "2",
    },
  ];

  const actionFilter = [
    {
      label: text.user.user_role,
      key: "role",
    },
    {
      label: text.all.profile,
      key: "profile",
    },
  ];

  const handleAction = (key: string) => {
    switch (key) {
      case "1":
        setDisplayUser(true);
        break;
      case "2":
        isToDelete(true);
        break;
      default:
        break;
    }
  };

  const handleFilter = (key: string) => {
    switch (key) {
      case "role":
        break;
      case "profile":
        break;
      default:
        break;
    }
  };
  const tableAction = React.useMemo(
    () => ({
      title: "Actions",
      field: "",
      render: (item: CompanyUser) => {
        return <MenuActions actions={actionData} row={item} onRowMenuClick={handleAction} menuButton />;
      },
    }),
    []
  );
  React.useEffect(() => {
    if (!loading && client) {
      queryUsers({
        variables: {
          params: { clientID: client._id },
        },
      });
    }
  }, [loading, client, queryUsers]);

  React.useEffect(() => {
    setUser(usersData?.companyUsersByClient?.data?.companyUsers[0]);
  }, [refetch, usersData]);

  const doDeletion = () => {
    isToDelete(false);
    deleteCompanyUser({
      variables: {
        input: { id: user._id, clientID: client._id },
      },
    })
      .then((response) => {
        const { code, message } = response.data?.deleteCompanyUser;
        dispatchAlert({
          message,
          severity: code === 200 ? "success" : "warning",
        });
        refetch();
      })
      .catch((response) => {
        const { message } = response.data?.deleteCompanyUser;
        dispatchAlert({ message });
        refetch();
      });
    isToDelete(false);
  };
  const AddUser = () => {
    setOpenAdd(true);
  };

  const onRowClick = (row: CompanyUser) => {
    setUser(row);
    setOpenDrawer(Boolean(row));
  };

  const dismiss = () => {
    setDisplayUser(false);
  };

  const InterActifView = React.useCallback(
    () => (
      <UpdateUser user={user} loading={loading} dismiss={dismiss} refetch={refetch} isSmallScreen={isSmallScreen} />
    ),
    [isSmallScreen, loading, refetch, user]
  );

  const handleDeletion = () => doDeletion();

  const handleCloseDeletion = () => isToDelete(false);

  return (
    <div className={styles.root}>
      <div
        className={styles.left}
        style={{
          width: isSmallScreen ? "100%" : "70%",
        }}
      >
        <Grid container spacing={1} direction="row" className={style.header}>
          <Grid item xs={2}>
            <Button
              color="primary"
              variant="contained"
              onClick={() => {
                update(false);
                AddUser();
              }}
              disabled={isCurrentUser}
            >
              {text.user.add_new_user}
            </Button>
          </Grid>
          <Grid item xs={4}>
            <Typography className={style.tag} variant="h3">
              {text.all.manage_your_team} <b>{text.all.easily}</b>
            </Typography>
          </Grid>

          <Grid item xs={6} className={style.headerIcon}>
            <NewUserIcon />
          </Grid>
        </Grid>
        <Grid container className={style.body}>
          <Grid item xs={6}>
            <Typography className={style.bodyTitle}>{text.all.users}</Typography>
          </Grid>
          <Grid className={style.filterStyle} item xs={6}>
            <SearchBox className={style.searchText} onChange={onSearch} />
            <div className={style.filterButton}>
              <FilterGroup onClick={handleFilter} actions={actionFilter} />
            </div>
          </Grid>
          <Grid item xs={12}>
            <div className={styles.table}>
              <Table
                tableWidth="99%"
                tableHeight="52vh"
                rowsIdentifier="_id"
                data={usersData?.companyUsersByClient?.data?.companyUsers}
                columns={[...UserColumns, tableAction]}
                isLoading={userLoading}
                onRowClick={onRowClick}
                setRowsPerPage={setRowsPerPage}
                rowsPerPageOptions={ROW_PER_PAGE_OPTIONS}
                rowsPerPage={rowsPerPage}
                currentPage={page}
                countTotal={totalCount}
              />
            </div>
          </Grid>
        </Grid>
      </div>
      <Slider
        openDrawer={openDrawer}
        handleClose={() => {
          setOpenDrawer(true);
        }}
      >
        <div className={styles.right}>{displayuser ? <InterActifView /> : <DefaultInfo />}</div>
      </Slider>
      <AddUserDialog
        isUpdate={isUpdate}
        user={userToUpdate}
        open={openAdd}
        handleClose={() => {
          setOpenAdd(false);
          update(false);
        }}
      />
      <Dialog
        open={isDelete}
        confirmButtonTitle={text.all.validate}
        handleClose={handleCloseDeletion}
        handleConfirm={handleDeletion}
        cancelButtonTitle={text.all.cancel}
        cancelButtonStyle={style.cancelButtonModal}
        footerStyle={style.footerStyleModal}
      >
        <Typography className={style.textDeleteModal}>{text.all.want_to_delete}</Typography>
      </Dialog>
    </div>
  );
}

export default Users;
